import {
  createContext,
  FC,
  PropsWithChildren,
  Suspense,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Transition } from '@headlessui/react';
import {
  useGetConversation,
  useGetConversationMessages,
} from 'modules/account/copilot/hooks';
import { MdClose } from 'react-icons/md';
import ChatHistory from 'ui/components/common/Copilot/ChatHistory';
import CopilotChat from 'ui/components/common/Copilot/CopilotChat';
import { cn } from 'utils/helpers';
import { useLocation } from 'react-router-dom';

import { TCopilotContext } from './useCopilotContext';

export const CopilotContext = createContext<TCopilotContext>({
  mode: 'chat',
  topic: '',
  selectedConversation: '',
  toggleCopilotSidebar: () => {},
  newChat: () => {},
  setSelectedConversation: () => {},
  messages: [],
  toggleMode: () => {},
  isLoading: false,
  setTopic: () => {},
  module: '',
  recordId: '',
});

const Copilot: FC<PropsWithChildren> = ({ children }) => {
  const [selectedConversation, setSelectedConversation] = useState<
    string | undefined
  >(undefined);
  const [topic, setTopic] = useState('');
  const [mode, setMode] = useState<'search' | 'chat'>('chat');
  const [isOpen, setIsOpen] = useState(false);

  const location = useLocation();

  const { data: activeConversation } = useGetConversation(selectedConversation);
  const { data: toadMessages, isFetching: messagesLoading } =
    useGetConversationMessages(selectedConversation);

  const messages = useMemo(() => toadMessages ?? [], [toadMessages]);

  const { module, recordId } = useMemo<{
    module: '' | 'leads' | 'deals';
    recordId: string;
  }>(() => {
    const isPreview = location.pathname.includes('preview');
    let recordId = '';
    let module: 'leads' | 'deals' | '' = '';

    if (location.pathname.includes('leads')) {
      module = 'leads';
      if (isPreview) {
        if (!location.pathname.endsWith('leads')) {
          recordId = location.pathname.split('/')?.at(-1) ?? '';
        }
      } else {
        recordId = location.pathname.split('/')?.[3] ?? '';
      }
    }

    return {
      module,
      recordId,
    };
  }, [location.pathname]);

  useEffect(() => {
    if (activeConversation) {
      setTopic(activeConversation?.data?.title);
    }
  }, [activeConversation]);

  useEffect(() => {
    if (
      location.pathname.includes('toadai') ||
      !location.pathname.includes('leads') ||
      !location.pathname.includes('leads')
    ) {
      setIsOpen(false);
    }
  }, [location.pathname]);

  const toggleCopilotSidebar = () => {
    setIsOpen((prev) => !prev);
  };

  const newChat = () => {
    setSelectedConversation('');
    setTopic('');
  };

  const toggleMode = () => {
    setMode((prev) => (prev === 'chat' ? 'search' : 'chat'));
  };

  const value = useMemo(
    () => ({
      mode,
      topic,
      selectedConversation,
      toggleCopilotSidebar,
      newChat,
      setSelectedConversation,
      messages,
      toggleMode,
      isLoading: messagesLoading,
      setTopic,
      module,
      recordId,
    }),
    [
      messages,
      messagesLoading,
      mode,
      module,
      recordId,
      selectedConversation,
      topic,
    ],
  );

  return (
    <CopilotContext.Provider value={value}>
      <div className="flex h-full">
        <div className="flex-1 h-full">{children}</div>
        <CopilotSidebar
          mode={mode}
          open={isOpen}
          toggle={toggleCopilotSidebar}
        />
      </div>
    </CopilotContext.Provider>
  );
};

export default Copilot;

const CopilotSidebar: FC<{
  open: boolean;
  toggle: () => void;
  mode: 'search' | 'chat';
}> = ({ open, toggle, mode }) => {
  return (
    <Transition
      className={cn('fixed top-0 right-0 z-[49] h-full')}
      show={open}
      enter="transform transition ease-in-out duration-300"
      enterFrom="translate-x-full"
      enterTo="translate-x-90"
      leave="transform transition ease-in-out duration-300"
      leaveFrom="translate-x-90"
      leaveTo="translate-x-full"
    >
      <div
        className={cn(
          'bg-fill-primary pl-4 pr-[18px] py-[9px] min-w-[440px]',
          'flex justify-between items-center',
        )}
      >
        <div className="font-bold sticky top-0 text-sm leading-[22px] tracking-[0.04em]">
          Toad Copilot
        </div>
        <MdClose
          role="button"
          onClick={toggle}
          className="w-4 h-4 hover:text-danger-base transition"
        />
      </div>
      <div className="h-[calc(100%-40px)] bg-fill-tertiary">
        <Suspense fallback={null}>
          {mode === 'search' ? <ChatHistory /> : <CopilotChat />}
        </Suspense>
      </div>
    </Transition>
  );
};
